import React, { useState } from 'react';
import { updateEventMeeting, removeEventMeeting } from '../utils/api';

// Duration options in minutes
const DURATIONS = [15, 30, 45, 60, 90, 120];

export default function MeetingScheduler({ eventId, contactId, meeting, venue, readOnly = false, onUpdate }) {
  const hasMeeting = !!(meeting?.meeting_date && meeting?.meeting_time);

  const [editing, setEditing] = useState(!hasMeeting);
  const [form, setForm] = useState({
    meeting_date: meeting?.meeting_date || '',
    meeting_time: meeting?.meeting_time || '',
    meeting_duration: meeting?.meeting_duration || 30,
    meeting_location: meeting?.meeting_location || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleConfirm = async () => {
    if (!form.meeting_date || !form.meeting_time) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await updateEventMeeting(eventId, contactId, {
        ...form,
        meeting_synced: false,
      });
      onUpdate && onUpdate(updated);
      setEditing(false);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    setSaving(true);
    try {
      await removeEventMeeting(eventId, contactId);
      setForm({ meeting_date: '', meeting_time: '', meeting_duration: 30, meeting_location: '' });
      setEditing(true);
      onUpdate && onUpdate(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSync = async () => {
    setSaving(true);
    try {
      const updated = await updateEventMeeting(eventId, contactId, {
        ...form,
        meeting_date: meeting?.meeting_date,
        meeting_time: meeting?.meeting_time,
        meeting_duration: meeting?.meeting_duration,
        meeting_location: meeting?.meeting_location,
        meeting_synced: true,
      });
      onUpdate && onUpdate(updated);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const formatTime = (t) => {
    if (!t) return '';
    const [h, m] = t.split(':');
    const hour = parseInt(h);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const h12 = hour % 12 || 12;
    return `${h12}:${m} ${ampm}`;
  };

  const formatDate = (d) => {
    if (!d) return '';
    const date = new Date(d + 'T00:00:00');
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  if (readOnly && hasMeeting) {
    return (
      <div className="meeting-confirmed readonly">
        <span className="meeting-pill confirmed">
          {formatDate(meeting.meeting_date)} · {formatTime(meeting.meeting_time)} · {meeting.meeting_duration}m
        </span>
        {meeting.meeting_location && (
          <div className="meeting-location">{meeting.meeting_location}</div>
        )}
      </div>
    );
  }

  if (readOnly && !hasMeeting) {
    return <span style={{ color: 'var(--color-text-muted)', fontSize: '0.8rem' }}>No meeting scheduled</span>;
  }

  if (!editing && hasMeeting) {
    return (
      <div className="meeting-confirmed">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span className="meeting-pill confirmed">
            {formatDate(meeting.meeting_date)} · {formatTime(meeting.meeting_time)} · {meeting.meeting_duration}m
          </span>
          {meeting.meeting_synced && (
            <span className="meeting-pill synced">Outlook + Teams</span>
          )}
        </div>
        {meeting.meeting_location && (
          <div className="meeting-location">{meeting.meeting_location}</div>
        )}
        <div className="meeting-actions">
          {!meeting.meeting_synced && (
            <button className="btn-link-sm" onClick={handleSync} disabled={saving}>
              Sync to Outlook
            </button>
          )}
          <button className="btn-link-sm" onClick={() => setEditing(true)} disabled={saving}>
            Edit
          </button>
          <button className="btn-link-sm danger" onClick={handleRemove} disabled={saving}>
            Remove
          </button>
        </div>
        {error && <div className="form-error" style={{ marginTop: 4 }}>{error}</div>}
      </div>
    );
  }

  // Scheduling form
  return (
    <div className="meeting-form">
      <div className="meeting-form-row">
        <input
          type="date"
          value={form.meeting_date}
          onChange={e => setForm(f => ({ ...f, meeting_date: e.target.value }))}
          className="input-sm"
          style={{ flex: '1 1 120px' }}
        />
        <input
          type="time"
          value={form.meeting_time}
          onChange={e => setForm(f => ({ ...f, meeting_time: e.target.value }))}
          className="input-sm"
          style={{ flex: '1 1 100px' }}
        />
        <select
          value={form.meeting_duration}
          onChange={e => setForm(f => ({ ...f, meeting_duration: parseInt(e.target.value) }))}
          className="input-sm"
          style={{ flex: '0 0 80px' }}
        >
          {DURATIONS.map(d => (
            <option key={d} value={d}>{d}m</option>
          ))}
        </select>
      </div>
      <input
        type="text"
        placeholder={venue || 'Location (optional)'}
        value={form.meeting_location}
        onChange={e => setForm(f => ({ ...f, meeting_location: e.target.value }))}
        className="input-sm"
        style={{ marginTop: 6, width: '100%', boxSizing: 'border-box' }}
      />
      <div className="meeting-form-row" style={{ marginTop: 8 }}>
        <button
          className="btn btn-sm btn-primary"
          onClick={handleConfirm}
          disabled={saving || !form.meeting_date || !form.meeting_time}
        >
          {saving ? 'Saving…' : 'Confirm'}
        </button>
        {hasMeeting && (
          <button className="btn-link-sm" onClick={() => setEditing(false)} disabled={saving}>
            Cancel
          </button>
        )}
      </div>
      {error && <div className="form-error" style={{ marginTop: 4 }}>{error}</div>}
    </div>
  );
}
